<template>
  <div
    v-if="isLyftUnavailable"
    class="estimation-card no-support-lyft"
  >
    Lyft rides are not supported outside US.
  </div>

  <div
    v-else
    class="estimation-card"
    :class="{ 'selected': isSelected, 'disabled': isDisabled }"
  >
    <div class="estimation-card-row title">
      {{ rideName }} {{ isOverallWinner ? '(Best)' : isPriceWinner ? '(Cheaper)' : '' }}
    </div>
    <div
      v-if="hasDiscountPrice"
      class="estimation-card-row original-estimate"
    >
      <i class="fa fa-close" /> {{ rideEstimate.estimate }}
    </div>
    <el-tooltip
      placement="left"
      :disabled="!hasDiscountPrice || isDisabled"
    >
      <template #content>
        <template v-if="rideEstimate.expiringCreditInfo?.showMembershipCreditBreakdown">
          <div>Covered by Credit:</div>
          <p class="mb-2">
            <strong>${{ rideUtility.formatCreditRange(rideEstimate.expiringCreditInfo.coveredByCreditMin, rideEstimate.expiringCreditInfo.coveredByCreditMax) }}</strong>
          </p>
          <div>Caller Pays:</div>
          <p class="mb-2">
            <strong>${{ rideUtility.formatCreditRange(rideEstimate.expiringCreditInfo.callerPaysMin, rideEstimate.expiringCreditInfo.callerPaysMax) }}</strong>
          </p>
        </template>
        <template v-else-if="hasExpiringCreditsAttention">
          <div>Caller Expiring Credits:</div>
          <p class="mb-2">
            <strong>{{ expiringCreditInfoText }}</strong>
          </p>
        </template>
        <template v-else-if="!!selectedOfferPromo">
          <div>Offered Promo:</div>
          <p class="mb-2">
            <strong>{{ selectedOfferPromo.discountText }}</strong>
          </p>
        </template>

        <template v-if="rideEstimate.partnerSubsidyEstimate">
          <div>Estimated Partner Subsidy:</div>
          <p><strong>${{ rideEstimate.partnerSubsidyEstimate }}</strong></p>
        </template>
      </template>
      <div
        class="estimation-card-row"
        :class="{ 'offered-promo-estimate': isOfferedPromoInEstimate }"
      >
        <i :class="isPriceWinner ? 'fa fa-check-square' : 'fa fa-square'" /> {{ estimateText }}
      </div>
    </el-tooltip>
    <div class="estimation-card-row">
      <i :class="isEtaWinner ? 'fa fa-check-square' : 'fa fa-square'" /> {{ rideEstimate.etaText }}
    </div>
    <p-button
      :type="isOverallWinner ? 'purple' : 'yellow'"
      round
      :disabled="isDisabled"
      @click="$emit('click')"
    >
      Select {{ rideEstimate.rideType }}
    </p-button>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue';
import type { RideEstimate } from '@/models/ride';
import rideUtility from '@/Utils/rideUtility';
import { RideType, VendorName } from '@/models/rideType';
import { selectedOfferPromo } from '@/services/operator-first-ride-offer-inventory/viewModel';

defineEmits(['click']);

const props = defineProps({
  rideEstimate: {
    type: Object as PropType<RideEstimate>,
    required: true,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  isPriceWinner: {
    type: Boolean,
    default: false,
  },
  isEtaWinner: {
    type: Boolean,
    default: false,
  },
  isOverallWinner: {
    type: Boolean,
    default: false,
  },
  isLyftUnavailable: {
    type: Boolean,
    default: false,
  },
});

const isDisabled = computed(() => props.rideEstimate.eta === null);
const rideName = computed(() => {
  if (RideType.findByKey(props.rideEstimate.rideType)?.vendor === VendorName.NASH) {
    return `${props.rideEstimate.providerName} via ${props.rideEstimate.rideType}`
  }
  return props.rideEstimate.rideType
})

const hasExpiringCreditsAttention = computed(
  () => Boolean(props.rideEstimate.expiringCreditInfo?.minAmount || props.rideEstimate.expiringCreditInfo?.maxAmount),
);
// same condition getFinalEstimateTxt uses to take the offered promo off the price
const isOfferedPromoInEstimate = computed(() => Boolean(selectedOfferPromo.value?.discount) && !hasExpiringCreditsAttention.value);
const hasDiscountPrice = computed(() => Boolean(hasExpiringCreditsAttention.value || props.rideEstimate.partnerSubsidyEstimate || selectedOfferPromo.value?.discount));

const estimateText = computed(() => rideUtility.getFinalEstimateTxt({
  rideEstimate: props.rideEstimate,
  offeredPromoCost: selectedOfferPromo.value?.discount ?? 0,
}));

const expiringCreditInfoText = computed(() => {
  if (!hasExpiringCreditsAttention.value) {
    return null;
  }

  const { description } = props.rideEstimate.expiringCreditInfo ?? {};

  if (description?.startsWith('new_user_first_ride_10_offer')) {
    return 'This caller gets $10 off this ride';
  }

  return description;
});
</script>

<style lang="scss" scoped>
.estimation-card {
  display: flex;
  flex-direction: column;
  padding: 8px 20px;
  border-radius: 5px;
  min-width: 0;
  border: 1px solid #c1c1c1;

  &.no-support-lyft {
    height: 100%;
    justify-content: center;
    text-align: center;
    font-weight: bold;
  }

  button {
    align-self: center;
  }

  &.selected {
    background: #d3ccff;
  }

  &.disabled {
    background: #eee;
    color: #999;

    button {
      cursor: no-drop;
    }
  }

  &.loading {
    font-style: italic;
    justify-content: center;
    align-items: center;
  }

  .estimation-card-row {
    margin-bottom: 5px;
    padding-left: 10px;

    &.title {
      font-weight: 700;
      margin-bottom: 10px;
    }

    &:has(+ button) {
      margin-bottom: 12px;
    }

    &.offered-promo-estimate {
      color: #047857;
      font-weight: 600;
    }

    &.original-estimate {
      color: #999;
      text-decoration: line-through;
    }
  }

  i.fa-check-square {
    color: #00c800;
  }

  i.fa-square {
    color: #ddd;
  }
}
</style>
